import { useEffect, useState } from "react";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { Box, IconButton, Stack, SxProps, Theme, Typography } from "@mui/material";
import { RemoveCircle, DragHandle } from "@mui/icons-material";
import createSlug from "../../App/Utilities/createSlug";

export const DraggableDropdownOptions = ({
  dropdownOptions,
  removeListItem,
  setDropdownList,
  disableOrder,
}: {
  dropdownOptions: string[];
  removeListItem(value: string): void;
  setDropdownList(list: string[]): void;
  disableOrder: boolean;
}) => {
  const [draftDropdownList, setDraftDropdownList] = useState<DraftDropdownItem[]>([]);

  const onDragEnd = (result: DropResult) => {
    const source = result.source?.index ?? 0;
    const dest = result.destination?.index ?? 0;

    // dropped outside the list
    if (!result.destination || dest === source) {
      return;
    }

    // no movement
    if (dest === source) {
      return;
    }

    const reorderedOptions = [...draftDropdownList];

    reorderedOptions.sort(sortDraftDropdowns).forEach((item, index) => {
      // set the new one
      if (index === source) {
        item.index = dest;
      }
      // moving down
      else if (dest > source) {
        // item is before destination, move it up
        if (index <= dest && index >= source) {
          item.index -= 1;
        }
        // item is after destination, move it down
        else if (index > dest && index <= source) {
          item.index += 1;
        }
      }
      // moving up
      else {
        // item is before destination, move it down
        if (index >= dest && index <= source) {
          item.index += 1;
        }
        // item is after destination move it up
        else if (index < dest && index >= source) {
          item.index -= 1;
        }
      }
    });

    setDropdownList(reorderedOptions.sort(sortDraftDropdowns).map((item) => item.content));
  };

  useEffect(() => {
    setDraftDropdownList(dropdownOptions.map((item, index) => ({ index, content: item })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownOptions]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable-dropdown-options" isDropDisabled={disableOrder}>
        {(provided, snapshot) => (
          <Box
            {...provided.droppableProps}
            ref={provided.innerRef}
            sx={getListStyle(snapshot.isDraggingOver, draftDropdownList.length)}
          >
            {draftDropdownList.sort(sortDraftDropdowns).map((item, index) => {
              const slug = `d-${createSlug(item.content)}-${index}`;
              return (
                <Draggable
                  draggableId={slug}
                  index={index}
                  key={slug}
                  isDragDisabled={disableOrder}
                  children={(provided, snapshot) => (
                    <Stack
                      direction="row"
                      key={`${slug}-item`}
                      ref={provided.innerRef}
                      id={`dropdown-option-${index}`}
                      sx={getListItemStyle(snapshot.isDragging)}
                      aria-label={`Drag ${item.content} to reorder`}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <IconButton
                        title="Remove dropdown option"
                        aria-label="remove item"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeListItem(item.content);
                        }}
                      >
                        <RemoveCircle />
                      </IconButton>
                      <Typography sx={{ padding: "8px", width: "100%" }} key={index}>
                        {item.content}
                      </Typography>
                      {!disableOrder && (
                        <IconButton
                          tabIndex={-1}
                          sx={{ "&:hover": { backgroundColor: "#ffffff", cursor: "grab" } }}
                          disableRipple={true}
                          disableTouchRipple={true}
                          disableFocusRipple={true}
                        >
                          <DragHandle />
                        </IconButton>
                      )}
                    </Stack>
                  )}
                />
              );
            })}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const getListStyle = (isDraggingOver: boolean, totalRowCount: number): SxProps<Theme> => ({
  border: totalRowCount > 0 ? "1px solid" : "0",
  borderColor: "lightgrey",
  padding: 0,
  background: isDraggingOver ? "rgb(158, 224, 186)" : "lightgrey",
});

const getListItemStyle = (isDragging: boolean): SxProps<Theme> => ({
  backgroundColor: "white",
  boxShadow: isDragging ? 10 : 0,
});

const sortDraftDropdowns = (a: DraftDropdownItem, b: DraftDropdownItem) => a.index - b.index;

interface DraftDropdownItem {
  index: number;
  content: string;
}
